import { useEffect, useState } from "react";
import { useApi } from "@/lib/api";

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }

  if (email) {
    return email[0]?.toUpperCase() ?? "?";
  }

  return "?";
}

const sizeClasses = {
  sm: "h-5 w-5 text-[9px]",
  md: "h-7 w-7 text-[11px]",
  xl: "h-16 w-16 text-xl",
  lg: "h-32 w-32 text-5xl",
} as const;

const avatarUrls = new Map<string, Promise<string | null>>();

function useAvatarUrl(avatarId: string | null | undefined) {
  const api = useApi();
  const [loaded, setLoaded] = useState<{ id: string; url: string } | null>(
    null,
  );

  useEffect(() => {
    if (!avatarId) {
      return;
    }

    let cancelled = false;
    let request = avatarUrls.get(avatarId);

    if (!request) {
      request = api
        .getBinary(`/avatars/${avatarId}`)
        .then((image) =>
          image ? URL.createObjectURL(new Blob([image])) : null,
        );

      avatarUrls.set(avatarId, request);
    }

    void request.then((url) => {
      if (!url) {
        avatarUrls.delete(avatarId);
      }

      if (!cancelled && url) {
        setLoaded({ id: avatarId, url });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [api, avatarId]);

  return loaded && loaded.id === avatarId ? loaded.url : null;
}

export function Avatar({
  name,
  email,
  avatarId,
  size = "sm",
}: {
  name: string | null;
  email: string | null;
  avatarId?: string | null;
  size?: keyof typeof sizeClasses;
}) {
  const url = useAvatarUrl(avatarId);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (url && url !== failedUrl) {
    return (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className={`shrink-0 rounded-full bg-surface object-cover ${sizeClasses[size]}`}
        onError={() => {
          if (avatarId) {
            avatarUrls.delete(avatarId);
          }

          setFailedUrl(url);
        }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-accent font-semibold leading-none text-surface ${sizeClasses[size]}`}
    >
      {getInitials(name, email)}
    </span>
  );
}
